<template>
  <div
    :class="layoutClasses.wrapper()"
    :style="{ background: backgroundGradient }"
    @keydown.esc="closeWindow"
  >
    <div id="view-with-header-content" :class="layoutClasses.content()">
      <div
        class="header-wrap sticky top-0 z-[60] transition-all"
        :class="{
          expanded: !isHeaderCollapsed,
          collapsed: isHeaderCollapsed,
          'custom-header-shadow': isHeaderCollapsed,
          ...opacityClass,
        }"
      >
        <template v-if="isEnabledWidgetV2">
          <ChatHeaderExpandedV2
            v-if="!isHeaderCollapsed"
            :intro-heading="channelConfig.welcomeTitle"
            :intro-body="channelConfig.welcomeTagline"
            :avatar-url="channelConfig.avatarUrl"
            :show-popout-button="appConfig.showPopoutButton"
          />
        </template>

        <template v-else>
          <chat-header-expanded
            v-if="!isHeaderCollapsed"
            :intro-heading="channelConfig.welcomeTitle"
            :intro-body="channelConfig.welcomeTagline"
            :avatar-url="channelConfig.avatarUrl"
            :show-popout-button="appConfig.showPopoutButton"
          />
        </template>

        <template v-if="isEnabledWidgetV2">
          <ChatHeaderV2
            v-if="isHeaderCollapsed"
            :title="channelConfig.websiteName"
            :avatar-url="channelConfig.avatarUrl"
            :show-popout-button="appConfig.showPopoutButton"
            :available-agents="availableAgents"
            :show-back-button="showBackButton"
          />
        </template>

        <template v-else>
          <chat-header
            v-if="isHeaderCollapsed"
            :title="channelConfig.websiteName"
            :avatar-url="channelConfig.avatarUrl"
            :show-popout-button="appConfig.showPopoutButton"
            :available-agents="availableAgents"
            :show-back-button="showBackButton"
          />
        </template>
      </div>
      <banner />
      <transition
        v-if="isEnabledWidgetV2"
        :css="false"
        mode="out-in"
        @enter="onEnter"
        @leave="onLeave"
      >
        <router-view :key="$route.name" />
      </transition>
      <router-view v-else />

      <div v-if="!isEnabledWidgetV2" class="py-1.5" />
    </div>
  </div>
</template>
<script>
import { tv } from 'tailwind-variants';
import Banner from '../Banner.vue';
import ChatHeader from '../ChatHeader.vue';

import ChatHeaderExpanded from '../ChatHeaderExpanded.vue';
import configMixin from '../../mixins/configMixin';
import { mapGetters } from 'vuex';
import { IFrameHelper } from 'widget/helpers/utils';
import { useFlipper } from 'widget/composables/useFlipper';
import { widgetGradient } from '../../../sdk/sdk';
import { animate } from 'motion/mini';

// V2
import ChatHeaderV2 from 'widget/v2/components/message-parts/ChatHeader.vue';
import ChatHeaderExpandedV2 from 'widget/v2/components/message-parts/ChatHeaderExpanded.vue';

const viewWithHeaderRecipe = tv({
  slots: {
    wrapper: 'w-full bg-slate-25 dark:bg-slate-800',
    content: 'flex flex-col relative pb-3',
  },
  variants: {
    version: {
      v1: {
        wrapper: 'h-full',
        content: 'h-full',
      },
      v2: {
        content: 'view-with-header-content',
      },
    },
    isOnHomeView: {
      true: {
        wrapper: 'overflow-auto',
      },
      false: {},
    },
    isMobile: {
      true: {
        wrapper: 'h-full',
        content: 'h-full justify-between',
      },
      false: {
        wrapper: '',
        content: '',
      },
    },
  },
  compoundVariants: [
    {
      version: 'v2',
      isOnHomeView: false,
      class: {
        wrapper: 'h-full',
        content: 'h-full justify-between',
      },
    },
  ],
  defaultVariants: {
    version: 'v1',
    isOnHomeView: true,
  },
});

export default {
  components: {
    Banner,
    ChatHeader,
    ChatHeaderExpanded,
    ChatHeaderV2,
    ChatHeaderExpandedV2,
  },
  mixins: [configMixin],
  setup() {
    const { isEnabled } = useFlipper();
    return { isFlipperEnabled: isEnabled };
  },
  data() {
    return {
      showPopoutButton: false,
      scrollPosition: 0,
      ticking: true,
      disableBranding: window.chatwootWebChannel.disableBranding || false,
    };
  },
  computed: {
    ...mapGetters({
      appConfig: 'appConfig/getAppConfig',
      availableAgents: 'agent/availableAgents',
      isMobile: 'appConfig/getIsMobile',
      widgetColor: 'appConfig/getWidgetColor',
      widgetGradientColor: 'appConfig/getWidgetGradientColor',
    }),
    portal() {
      return window.chatwootWebChannel.portal;
    },
    isHeaderCollapsed() {
      if (!this.hasIntroText) {
        return true;
      }
      return !this.isOnHomeView;
    },
    hasIntroText() {
      return (
        this.channelConfig.welcomeTitle || this.channelConfig.welcomeTagline
      );
    },
    showBackButton() {
      return ['article-viewer', 'messages', 'prechat-form'].includes(
        this.$route.name
      );
    },
    isOnArticleViewer() {
      return ['article-viewer'].includes(this.$route.name);
    },
    isOnHomeView() {
      return ['home'].includes(this.$route.name);
    },
    opacityClass() {
      if (this.isHeaderCollapsed) {
        return {};
      }
      if (this.scrollPosition > 30) {
        return { 'opacity-30': true };
      }
      if (this.scrollPosition > 25) {
        return { 'opacity-40': true };
      }
      if (this.scrollPosition > 20) {
        return { 'opacity-60': true };
      }
      if (this.scrollPosition > 15) {
        return { 'opacity-80': true };
      }
      if (this.scrollPosition > 10) {
        return { 'opacity-90': true };
      }
      return {};
    },
    backgroundGradient() {
      return widgetGradient(this.widgetGradientColor);
    },
    isEnabledWidgetV2() {
      return this.isFlipperEnabled('chat_widget_v2').value;
    },
    layoutClasses() {
      return viewWithHeaderRecipe({
        version: this.isEnabledWidgetV2 ? 'v2' : 'v1',
        isOnHomeView: this.isOnHomeView,
        isMobile: this.isMobile,
      });
    },
  },
  mounted() {
    this.$el.addEventListener('scroll', this.updateScrollPosition);
  },
  beforeDestroy() {
    this.$el.removeEventListener('scroll', this.updateScrollPosition);
  },
  methods: {
    closeWindow() {
      IFrameHelper.sendMessage({ event: 'closeWindow' });
    },
    updateScrollPosition(event) {
      this.scrollPosition = event.target.scrollTop;
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.ticking = false;
        });

        this.ticking = true;
      }
    },
    onEnter(el, done) {
      el.style.opacity = '0';
      el.style.transform = 'scale(0.98) translateY(32px)';

      animate(
        el,
        { opacity: 1, transform: 'scale(1) translateY(0)' },
        { duration: 0.1, easing: [0.4, 0, 0.2, 1] }
      ).then(done);
    },
    onLeave(el, done) {
      animate(
        el,
        { opacity: 1, transform: 'scale(0.98) translateY(-32px)' },
        { duration: 0, easing: [0.4, 0, 1, 1] }
      ).then(done);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~widget/assets/scss/variables';
@import '~widget/assets/scss/mixins';

.custom-header-shadow {
  @include shadow-large;
}

.header-wrap {
  flex-shrink: 0;
  transition: max-height 100ms;

  &.expanded {
    max-height: 16rem;
  }

  &.collapsed {
    max-height: 4.5rem;
  }

  @media only screen and (min-device-width: 320px) and (max-device-width: 667px) {
    border-radius: 0;
  }
}
</style>
